import { initSmoothScroll } from '../utils/utils';
import type { SpheresBackground } from './animation';
import { initCarousel } from './carousel';
import { initHeroLetters } from './hero-letters';
import { loadCertificates, loadExperiences } from './data-loader';
import { hideLoadingOverlay, updateLoading } from './loading';
import { animateNavigationOnScroll } from './navigation';
import { initOverlay } from './overlay';
import { fetchProjects } from './projects';
import { initLocalTime, initReveals } from './reveal';

/**
 * Ambient spheres background - loaded lazily after the site is interactive,
 * and only on capable devices. The section observer feeds it palette changes
 * once it exists.
 */
let ambient: SpheresBackground | null = null;
let ambientSection = 'hero';

function loadAmbientBackground(): void {
  const reducedMotion = matchMedia('(prefers-reduced-motion: reduce)').matches;
  const finePointer = matchMedia('(pointer: fine)').matches;
  if (reducedMotion || !finePointer) return;

  const start = async () => {
    const { loadAnimation } = await import('./animation');
    // Backdrop lines only - the spheres were too busy for a background
    ambient = loadAnimation(null, { spheres: false, maxOpacity: 0.6 });
    ambient.setTargetPalette(ambientSection);
  };
  // Defer to idle time so it never competes with the carousel or LCP
  if ('requestIdleCallback' in window) {
    requestIdleCallback(() => void start(), { timeout: 3000 });
  } else {
    setTimeout(() => void start(), 1500);
  }
}

/**
 * Initialize all site features
 */
async function initSite(): Promise<void> {
  updateLoading(true, 0);

  await loadExperiences();
  updateLoading(true, 25);

  await loadCertificates();
  updateLoading(true, 50);

  initSmoothScroll();
  initLocalTime();
  updateLoading(true, 65);

  const projects = await fetchProjects();
  const openProject = initOverlay(projects);
  initCarousel(projects, openProject);
  updateLoading(true, 85);

  // Deep links: #work/<slug> opens the project overlay directly
  const openFromHash = () => {
    const slug = location.hash.match(/^#work\/(.+)$/)?.[1];
    if (slug) openProject(decodeURIComponent(slug));
  };
  window.addEventListener('hashchange', openFromHash);
  openFromHash();

  animateNavigationOnScroll((sectionId) => {
    ambientSection = sectionId;
    ambient?.setTargetPalette(sectionId);
  });
  initReveals();
  initHeroLetters();
  updateLoading(true, 100);

  // Small delay before hiding the loader for a smooth transition
  setTimeout(() => {
    updateLoading(false, 100);
    hideLoadingOverlay();
    loadAmbientBackground();
  }, 500);
}

document.addEventListener('DOMContentLoaded', initSite);
